import * as uiAnalyticsConsumer from '@servicenow/ui-analytics-consumer';
import {
	mark,
	getInteractionId,
	types as metrics,
	createInteractionId
} from '@servicenow/ui-metrics';
import {getHostInfo, allLogTypes as logTypes} from '@servicenow/ui-internal';
import {customEventDispatcher} from './customEventDispatcher';
import {
	createMetaData,
	appendToPayload,
	isPropertyUpdate,
	normalizeActionMeta,
	validateActionPayload,
	isInternalActionType,
	getInternalActionName,
	isStoreUpdate,
	hasActionHandlerForType,
	skipForDetachedComponent
} from './utils';
import {
	HELPERS_SCHEDULED,
	HELPERS_IMMEDIATE,
	PRESENTATIONAL,
	ANCESTOR_PATH,
	INTERNAL_HOST_DISPATCH
} from '../symbols';
import {emitNowUiEvent, getHostVariables, getSpanOptions} from '../utils';
import log from '../log';
import {
	ACTION_DISPATCHED,
	SCHEDULE_IMMEDIATELY_DO_NOT_USE_OR_YOU_WILL_BE_IMMEDIATELY_FIRED,
	PROPERTIES_SET,
	DISPATCH_EVENT,
	SPAN_FOR_DISPATCH,
	SPAN_KIND_CLIENT,
	SPAN_ATTR_DISPATCH_ACTION_TYPE,
	SPAN_ATTR_DISPATCH_UNDEFINED,
	SPAN_ATTR_DISPATCH_FLAG_HOST_DISPATCH,
	SPAN_ATTR_SKIP_FOR_DETACHED_COMPONENT,
	SPAN_ATTR_STORE_UPDATE,
	SPAN_ATTR_DISPATCH_ACTION_INTERNAL_TYPE,
	SPAN_ATTR_LEVEL,
	SPAN_LEVEL_FINEST
} from '../constants';
import {addActionHandlerForUnknownType} from './addActionHandlerForUnknownType';
const {trace, isValidTraceId} = uiAnalyticsConsumer;
export function createDispatcher(host, dispatcher = customEventDispatcher) {
	const hostVariables = getHostVariables(host);

	return function dispatch(type, payload = {}, meta = {}, error = null) {
		const isExternalHostDispatch = meta[INTERNAL_HOST_DISPATCH] !== true;
		let {span} = meta;
		if (isExternalHostDispatch) {
			span = trace(SPAN_FOR_DISPATCH, {
				...getSpanOptions(host),
				traceId: createInteractionId(),
				kind: SPAN_KIND_CLIENT,
				...(meta.span && isValidTraceId(meta.span.traceId) && {parentSpan: meta.span})
			});
			span.setAttribute(SPAN_ATTR_DISPATCH_ACTION_TYPE, type);
			span.setAttribute(SPAN_ATTR_DISPATCH_FLAG_HOST_DISPATCH, true);
			if (isValidTraceId(span.traceId)) meta.span = span;
		}
		if (type === undefined) {
			log(
				`You are dispatching an action that is type: ${type}.  We will send undefined actions to the upside down place in the next release.`,
				{level: logTypes.WARN, host, origin: 'dispatch'}
			);
			if (span) {
				span.setAttribute(SPAN_ATTR_DISPATCH_ACTION_TYPE, 'undefined');
				span.setAttribute(SPAN_ATTR_DISPATCH_UNDEFINED, true);
				span.setAttribute(SPAN_ATTR_LEVEL, SPAN_LEVEL_FINEST);
			}
		}

		if (skipForDetachedComponent(host, type, payload)) {
			span && span.setAttribute(SPAN_ATTR_SKIP_FOR_DETACHED_COMPONENT, true);
			span && span.setAttribute(SPAN_ATTR_LEVEL, SPAN_LEVEL_FINEST);
			if (isExternalHostDispatch) {
				span.end();
			}
			return;
		}

		if (isStoreUpdate(type)) {
			hostVariables[HELPERS_SCHEDULED].updateState(payload, {
				[SCHEDULE_IMMEDIATELY_DO_NOT_USE_OR_YOU_WILL_BE_IMMEDIATELY_FIRED]: true,
				interactionId: getInteractionId(meta)
			});
			span && span.setAttribute(SPAN_ATTR_STORE_UPDATE, true);
			if (isExternalHostDispatch) {
				span.end();
			}
			return log(
				`${type} is a private action type and has been deprecated. Please use updateState.`,
				{host, origin: 'dispatch'}
			);
		}

		if (isInternalActionType(type, meta.internal)) {
			const {tagName, componentId} = getHostInfo(host);
			log(`${getInternalActionName(type)} cannot be dispatched as it is an internal action type`, {
				tagName,
				componentId,
				host,
				origin: 'dispatch'
			});

			// Only return if the type is not one of the following, in order to maintain backwards compatibility
			if (![PROPERTIES_SET, DISPATCH_EVENT].includes(type)) {
				span && span.setAttribute(SPAN_ATTR_DISPATCH_ACTION_INTERNAL_TYPE, true);
				span && span.setAttribute(SPAN_ATTR_LEVEL, SPAN_LEVEL_FINEST);
				if (isExternalHostDispatch) {
					span.end();
				}
				return;
			}
		}

		if (isPropertyUpdate(type)) {
			if (isExternalHostDispatch) {
				span.end();
			}
			return hostVariables[HELPERS_IMMEDIATE].updateProperties(payload, {
				interactionId: getInteractionId(meta)
			});
		}

		validateActionPayload(host, type, payload, hostVariables); // TODO: fix

		// eslint-disable-next-line no-param-reassign
		meta = normalizeActionMeta(meta, {host});

		const action = {
			type,
			payload: meta.retargeted ? payload : appendToPayload(host, payload),
			meta: createMetaData(host, meta, hostVariables),
			error
		};

		emitNowUiEvent(ACTION_DISPATCHED, {
			host,
			action,
			ancestorPath: hostVariables?.[ANCESTOR_PATH]?.map(ancestorElement => {
				return {componentId: ancestorElement.nowId, componentName: ancestorElement.tagName};
			})
		});

		if (
			!hostVariables[PRESENTATIONAL] &&
			!meta.internal &&
			!hasActionHandlerForType(hostVariables, type)
		)
			addActionHandlerForUnknownType(host, type, hostVariables);

		mark(host, getInteractionId(action.meta), metrics.DISPATCH, {type});
		if (isExternalHostDispatch) {
			span.end();
		}
		return dispatcher(host, action);
	};
}
